import { useCallback, useEffect, useState } from 'react';
import type { Disputa, FiltrosPagos, FormaPago, ListaPagos as Lista, Pago, Resumen } from '../../../api/ops/pagos';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Campo } from '../../../componentes/Campo';
import { Chip } from '../../../componentes/Chip';
import { Desplegable } from '../../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../../componentes/EstadoPagina';
import { Filtros } from '../../../componentes/Filtros';
import { Icono } from '../../../componentes/Icono';
import { Paginacion } from '../../../componentes/Paginacion';
import { Tabla, type FilaTabla } from '../../../componentes/Tabla';
import { rolesDe } from '../../../logica/acceso';
import { dinero } from '../../../logica/dinero';
import { estadoDeError } from '../../../logica/estados';
import { diaYHora, hoyLocal } from '../../../logica/fechasCortas';
import { haySiguiente, usePaginacionCursor } from '../../../logica/paginacion';
import { useCabecera, type AccionCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { avisoRegistrado } from '../cobrar/avisos';
import { Registrar } from '../cobrar/Registrar';
import { useNegocioPagos } from '../negocio';
import { AYUDA, CONCEPTO, ESTADO_CON_CHIP, ESTADO_PAGO, FORMA_PAGO, PERMISOS, quedaPara, tipoCobrable } from '../textos';
import { FichaDePago } from './FichaDePago';

/**
 * Pagos y reembolsos (4.4, con el bloque de 4.5 en la ficha): arriba lo cobrado esta semana con la anterior al lado (de
 * `getPaymentsSummary` con `compare=previous`, G290: el panel no resta) y una fila de acción solo si una disputa vence;
 * filtros Todos · Pagados · Garantías · Devueltos · Por decidir sin cifras (G140; varios estados en `status`, G284; «Por
 * decidir» con `refund_decision_pending`, G285); periodo, dónde y forma de pago en «Ajustes de vista»; «Buscar por
 * cliente» (`listCustomers` → `customer_id`); tabla con «Anterior / Siguiente» (G121). El chip solo sale si hay que
 * actuar. `?pago=` abre la ficha (destino `pago` de G298). Nada de «Cobrar el no-show» ni «Canceló tarde» (G282).
 */
const FILTROS: readonly { etiqueta: string; filtros: Pick<FiltrosPagos, 'status' | 'refund_decision_pending'> }[] = [
    { etiqueta: 'Todos', filtros: {} },
    // G1017: «Pagados» sigue siendo `completed,processing`; lo `registered` (pagado fuera) sale en «Todos».
    { etiqueta: 'Pagados', filtros: { status: ['completed', 'processing'] } },
    { etiqueta: 'Garantías', filtros: { status: ['guarantee_saved', 'released'] } },
    { etiqueta: 'Devueltos', filtros: { status: ['partially_refunded', 'refunded'] } },
    { etiqueta: 'Por decidir', filtros: { refund_decision_pending: true } },
];

const PERIODOS = [
    { valor: 'todo', texto: 'Desde siempre' },
    { valor: 'hoy', texto: 'Hoy' },
    { valor: '7d', texto: 'Últimos 7 días' },
    { valor: 'mes', texto: 'Este mes' },
] as const;

function desdePeriodo(p: string, ahora: Date): string | undefined {
    const inicio = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
    if (p === 'hoy') {
        return inicio.toISOString();
    }
    if (p === '7d') {
        return new Date(inicio.getTime() - 6 * 86_400_000).toISOString();
    }
    if (p === 'mes') {
        return new Date(ahora.getFullYear(), ahora.getMonth(), 1).toISOString();
    }
    return undefined;
}

/** Lunes de esta semana en `AAAA-MM-DD`. */
function lunes(ahora: Date): string {
    const d = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return hoyLocal(d);
}

export function ListaPagos(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar, busqueda, ruta } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const ver = permisos.puede(PERMISOS.pagosVer);
    const verResumen = permisos.puede(PERMISOS.resumen);
    const verClientes = permisos.puede(PERMISOS.clientes);
    const puedeRegistrar = permisos.puede(PERMISOS.registrar);
    const n = useNegocioPagos(ops, negocio);

    const [filtro, setFiltro] = useState(() => (busqueda.get('filtro') === 'por-decidir' ? 4 : 0));
    const [periodo, setPeriodo] = useState('todo');
    const [origen, setOrigen] = useState('');
    const [forma, setForma] = useState('');
    const [cliente, setCliente] = useState<{ id: string; nombre: string } | null>(null);
    const [buscar, setBuscar] = useState('');
    const [encontrados, setEncontrados] = useState<{ id: string; nombre: string }[] | null>(null);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;
    const [cargada, setCargada] = useState<{ vista: string; lista: Lista | null; error: unknown } | null>(null);
    const [nombres, setNombres] = useState<Readonly<Record<string, string>>>({});
    const [resumen, setResumen] = useState<Resumen | null>(null);
    const [disputa, setDisputa] = useState<Disputa | null>(null);
    const [intento, setIntento] = useState(0);
    const [registrar, setRegistrar] = useState(false);
    const abierto = busqueda.get('pago');

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    const idCliente = cliente?.id;
    const vista = [String(filtro), periodo, origen, forma, idCliente ?? '', pag.cursor ?? ''].join('|');

    useEffect(() => {
        if (!ver) {
            return;
        }
        let vivo = true;
        const consulta: FiltrosPagos = {
            ...FILTROS[filtro]?.filtros,
            created_from: desdePeriodo(periodo, new Date()),
            origin: origen === '' ? undefined : (origen as FiltrosPagos['origin']),
            method: forma === '' ? undefined : (forma as FormaPago),
            customer_id: idCliente,
            sort: '-created_at',
            cursor: pag.cursor,
        };
        ops.listPayments(negocio, consulta)
            .then((l) => {
                if (vivo) {
                    setCargada({ vista, lista: l, error: null });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCargada((c) => ({ vista, lista: c?.vista === vista ? c.lista : null, error: e }));
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, ver, vista, filtro, periodo, origen, forma, idCliente, pag.cursor, intento]);

    // Nombres de los clientes de la página, en una sola llamada (G303).
    const lista = cargada?.vista === vista ? cargada.lista : null;
    const faltan = verClientes && lista !== null ? [...new Set(lista.data.map((p) => p.customer_id).filter((x): x is string => typeof x === 'string' && !(x in nombres)))] : [];
    const clavesFaltan = faltan.join(',');
    useEffect(() => {
        if (clavesFaltan === '') {
            return;
        }
        let vivo = true;
        ops.listCustomersByIds(negocio, clavesFaltan.split(','))
            .then((l) => {
                if (vivo) {
                    setNombres((x) => ({ ...x, ...Object.fromEntries(clavesFaltan.split(',').map((id) => [id, l.data.find((c) => c.id === id)?.display_name ?? ''])) }));
                }
            })
            .catch(() => {
                if (vivo) {
                    setNombres((x) => ({ ...x, ...Object.fromEntries(clavesFaltan.split(',').map((id) => [id, ''])) }));
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, clavesFaltan]);

    // Lo cobrado esta semana, con la anterior (G290), y la disputa que vence antes.
    useEffect(() => {
        if (!ver) {
            return;
        }
        let vivo = true;
        const ahora = new Date();
        if (verResumen) {
            ops.getPaymentsSummary(negocio, lunes(ahora), hoyLocal(ahora), true)
                .then((r) => {
                    if (vivo) {
                        setResumen(r);
                    }
                })
                .catch(() => undefined);
        }
        ops.listDisputes(negocio, { status: 'open', limit: 1 })
            .then((l) => {
                if (vivo) {
                    setDisputa(l.data[0] ?? null);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, ver, verResumen, intento]);

    const acciones: AccionCabecera[] = [];
    if (ver && puedeRegistrar) {
        acciones.push({ clave: 'registrar', texto: 'Registrar un pago', icono: 'banknote', desactivado: soloLectura, onClick: () => { setRegistrar(true); } });
    }
    useCabecera({ ayuda: ver ? AYUDA.pagos : [], acciones });

    if (!ver) {
        return <EstadoSinPermiso seccion="Pagos" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    const error = cargada?.vista === vista ? cargada.error : null;

    const abrir = (id: string | null) => {
        const p = new URLSearchParams(busqueda);
        if (id === null) {
            p.delete('pago');
        } else {
            p.set('pago', id);
        }
        navegar(`${ruta}?${p.toString()}`, true);
    };

    const buscarCliente = () => {
        const q = buscar.trim();
        if (q === '') {
            return;
        }
        ops.listCustomers(negocio, { q, limit: 5 })
            .then((l) => {
                setEncontrados(l.data.map((c) => ({ id: c.id, nombre: c.display_name })));
            })
            .catch(() => {
                setEncontrados([]);
            });
    };

    const ahora = new Date();
    const filas: FilaTabla[] = (lista?.data ?? []).map((p) => filaDePago(p, nombres, filtro === 4, ahora, movil, () => { abrir(p.id); }));
    const q = disputa === null ? null : quedaPara(disputa.evidence_due_by, ahora);

    const contenido = (() => {
        if (error !== null && lista === null) {
            const e = estadoDeError(error);
            return e.tipo === 'sin-permiso' ? (
                <EstadoSinPermiso seccion="Pagos" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />
            ) : (
                <EstadoError seccion="los pagos" codigoSoporte={e.codigoSoporte} onReintentar={recargar} movil={movil} />
            );
        }
        if (lista === null) {
            return <EstadoCargando seccion="los pagos" />;
        }
        return (
            <>
                <Tabla
                    etiqueta="Pagos"
                    movil={movil}
                    columnas={[
                        { clave: 'importe', titulo: 'Importe', ancho: '130px', alinear: 'derecha' },
                        { clave: 'cliente', titulo: 'Cliente', ancho: 'minmax(0, 1.3fr)' },
                        { clave: 'que', titulo: 'Qué se cobra', ancho: 'minmax(0, 1.8fr)' },
                        { clave: 'forma', titulo: 'Forma de pago', ancho: 'minmax(0, 1fr)' },
                        { clave: 'estado', titulo: 'Estado', ancho: '150px' },
                    ]}
                    filas={filas}
                    vacia={
                        <div className="rv-ca-vacia">
                            <span className="rv-ca-vacia__baldosa"><Icono nombre="credit-card" /></span>
                            <strong>{filtro === 0 && cliente === null && periodo === 'todo' ? 'Todavía no hay pagos' : 'No hay pagos con estos filtros'}</strong>
                            <span>{filtro === 0 && cliente === null && periodo === 'todo' ? 'Aquí verás lo que cobras online con Stripe y lo que registras en el local.' : 'Prueba con otro filtro u otro periodo.'}</span>
                        </div>
                    }
                />
                <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />
            </>
        );
    })();

    return (
        <div className="rv-ca-apartado">
            {resumen !== null && (
                <div className="rv-pg-titular">
                    <h2>
                        Has cobrado <em>{dinero(resumen.collected)}</em>
                    </h2>
                    <span>{`Esta semana${resumen.previous == null ? '' : ` · la anterior, ${dinero(resumen.previous.collected)}`}`}</span>
                </div>
            )}
            {disputa !== null && q !== null && (
                <div className="rv-pg-accion" role="status">
                    <span className="rv-pg-baldosa rv-pg-baldosa--suave"><Icono nombre="scale" /></span>
                    <span className="rv-ca-fila__texto">
                        <strong className="rv-pg-rojo">{q.vencida ? 'Una disputa ha llegado a su fecha límite' : `Una disputa vence en ${q.largo}`}</strong>
                        <span>{`${dinero(disputa.amount)} · ${disputa.bank_reason_label ?? disputa.bank_reason}`}</span>
                    </span>
                    <button type="button" className="rv-b rv-b-sec" onClick={() => { navegar(`${ruta}?apartado=disputas&disputa=${encodeURIComponent(disputa.id)}`); }}>
                        <span>Responder</span>
                        <Icono nombre="chevron-right" />
                    </button>
                </div>
            )}
            <Filtros etiqueta="Estado de los pagos" items={FILTROS} valor={filtro} onCambio={(i) => { setFiltro(i); reiniciar(); }} />
            <details className="rv-pg-plegable rv-pg-plegable--plano">
                <summary>
                    <span className="rv-ca-fila__texto"><strong>Ajustes de vista</strong><span>Periodo, dónde y forma de pago{verClientes ? ', y buscar por cliente' : ''}.</span></span>
                    <Icono nombre="sliders-horizontal" className="rv-pg-plegable__flecha" />
                </summary>
                <div className={`rv-pg-ajustes${movil ? ' rv-pg-ajustes--movil' : ''}`}>
                    <Desplegable etiqueta="Periodo" valor={periodo} opciones={PERIODOS} onChange={(v) => { setPeriodo(v); reiniciar(); }} />
                    <Desplegable etiqueta="Dónde" valor={origen} opciones={[{ valor: '', texto: 'Online y en el local' }, { valor: 'stripe', texto: 'Online, con Stripe' }, { valor: 'external', texto: 'En el local' }]} onChange={(v) => { setOrigen(v); reiniciar(); }} />
                    <Desplegable etiqueta="Forma de pago" valor={forma} opciones={[{ valor: '', texto: 'Todas' }, ...(Object.keys(FORMA_PAGO) as FormaPago[]).map((f) => ({ valor: f, texto: FORMA_PAGO[f][0] }))]} onChange={(v) => { setForma(v); reiniciar(); }} />
                </div>
                {verClientes && (
                    <div className="rv-ca-buscar">
                        {cliente === null ? (
                            <>
                                <Campo etiqueta="Buscar por cliente" tipo="search" valor={buscar} onChange={setBuscar} onEnter={buscarCliente} ayuda="Escribe el nombre, el teléfono o el email y pulsa Intro." />
                                {encontrados !== null && (encontrados.length === 0 ? (
                                    <span className="rv-ca-nota rv-ca-nota--gris">No encontramos a nadie con eso.</span>
                                ) : (
                                    <div className="rv-ca-resultados">
                                        {encontrados.map((c) => (
                                            <button key={c.id} type="button" className="rv-ca-resultado" onClick={() => { setCliente(c); setEncontrados(null); reiniciar(); }}>
                                                <span className="rv-ca-inicial">{c.nombre.charAt(0).toLocaleUpperCase('es')}</span>
                                                <span><strong>{c.nombre}</strong></span>
                                                <Icono nombre="chevron-right" />
                                            </button>
                                        ))}
                                    </div>
                                ))}
                            </>
                        ) : (
                            <div className="rv-ca-elegido">
                                <span className="rv-ca-inicial">{cliente.nombre.charAt(0).toLocaleUpperCase('es')}</span>
                                <span><strong>{cliente.nombre}</strong><small>Solo sus pagos</small></span>
                                <button type="button" className="rv-b rv-b-sec" onClick={() => { setCliente(null); setBuscar(''); reiniciar(); }}><Icono nombre="x" /><span>Quitar</span></button>
                            </div>
                        )}
                    </div>
                )}
            </details>
            {contenido}
            <FichaDePago
                {...props}
                id={abierto}
                nombres={nombres}
                onCerrar={() => { abrir(null); }}
                onCambiado={recargar}
            />
            {puedeRegistrar && (
                <Registrar
                    ops={ops}
                    negocio={negocio}
                    movil={movil}
                    abierto={registrar}
                    n={n}
                    onCerrar={() => { setRegistrar(false); }}
                    onRegistrado={(pago, cobrable) => {
                        setRegistrar(false);
                        avisar({ tipo: 'ok', texto: avisoRegistrado(pago, cobrable) });
                        recargar();
                    }}
                    onError={(texto) => { avisar({ tipo: 'error', texto }); }}
                />
            )}
        </div>
    );
}

function filaDePago(p: Pago, nombres: Readonly<Record<string, string>>, porDecidir: boolean, ahora: Date, movil: boolean, onAbrir: () => void): FilaTabla {
    const nombre = p.customer_id == null ? 'Sin cliente' : nombres[p.customer_id] || 'Cliente';
    const [tipo, icono] = tipoCobrable(p.payable.type);
    const que = [tipo, p.description ?? ''].filter((x) => x !== '').join(' · ');
    const devuelto = p.refunded_amount !== undefined && p.refunded_amount.amount > 0;
    const sinCargo = p.concept === 'guarantee' && p.amount.amount === 0;
    const chip: [string, Parameters<typeof Chip>[0]['estado']] | null = porDecidir ? ['Por decidir', 'pending'] : ESTADO_CON_CHIP.has(p.status) ? [ESTADO_PAGO[p.status][0], ESTADO_PAGO[p.status][1]] : null;
    const importe = (
        <span className="rv-pg-importe-celda">
            <strong className={`rv-pg-importe${sinCargo ? ' rv-pg-importe--gris' : ''}`}>{sinCargo ? 'Sin cargo' : dinero(p.amount)}</strong>
            {devuelto && <small>{`−${dinero(p.refunded_amount)} devuelto`}</small>}
        </span>
    );
    const forma = p.method == null ? '' : FORMA_PAGO[p.method][0];
    return {
        clave: p.id,
        etiqueta: `${sinCargo ? 'Sin cargo' : dinero(p.amount)}, ${nombre}. Abrir`,
        onAbrir,
        celdas: {
            importe,
            cliente: <span className="rv-ca-fila__texto"><strong>{nombre}</strong><span>{diaYHora(p.created_at, ahora)}</span></span>,
            que: <span className="rv-ca-fila__texto"><strong>{CONCEPTO[p.concept]}</strong><span className="rv-pg-con-icono"><Icono nombre={icono} />{que}</span></span>,
            forma: forma === '' ? null : <span className="rv-pg-con-icono"><Icono nombre={p.method == null ? 'credit-card' : FORMA_PAGO[p.method][1]} />{forma}</span>,
            estado: chip === null ? null : <Chip estado={chip[1]}>{chip[0]}</Chip>,
        },
        tarjeta: movil ? (
            <span className="rv-pg-tarjeta-pago">
                <span className="rv-ca-fila__texto">
                    <strong>{nombre}</strong>
                    <span>{[CONCEPTO[p.concept], que, diaYHora(p.created_at, ahora)].filter((x) => x !== '').join(' · ')}</span>
                    {chip !== null && <span><Chip estado={chip[1]}>{chip[0]}</Chip></span>}
                </span>
                {importe}
            </span>
        ) : undefined,
    };
}
